import { LoadingOutlined } from '@ant-design/icons';
import { Divider, Tooltip } from 'antd';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { PlusSvg } from '../../../../assets/icons';
import {
  allPromptOutputsSelector,
  selectedSlideIdSelector,
  TemplateAction,
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import useSinglePromptTestCase from '../../../../hooks/useSinglePromptTestCase';
import {
  useAvailablePromptDependencies,
  useVisiblePromptDependentInputValues,
  useVisiblePromptTestCaseInputs,
} from '../../../../hooks/useVisiblePromptTestCaseInputs';
import {
  AIResourceTypes,
  ById,
  Prompt,
  PromptInput,
  PromptModels,
  promptModels,
  PromptSingleTestCaseResultType,
  ResourceType,
} from '../../../../services/backendService/types';
import { reconnectPromptDependantElements } from '../../../../utils/aiGeneratorUtils';
import { Input } from '../../../_atoms/_Input';
import Select from '../../../_atoms/_Select/Select';
import { TextArea } from '../../../_atoms/_TextArea';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import GameImageRenderer from '../../../_molecules/GameImageRenderer';
import { SlideElement } from '../../../SlideComponents/slide.types';
import { createImageElement } from '../../../SlideComponents/slideUtil';
import {
  AIImageConnectorConfirmModal,
  PromptAIImageConnector,
} from './PromptAIImageConnector';
import { findDependencies } from './PromptBuilder';
import { PromptTextEditor } from './PromptTextEditor';

const PromptImageBuilder = ({
  prompt,
  localTemplateDispatch,
  promptInputs,
  setShowEditInputs,
}: {
  prompt: Prompt;
  promptInputs?: PromptInput[];
  localTemplateDispatch: Dispatch<TemplateAction>;
  setShowEditInputs: Dispatch<SetStateAction<boolean>>;
}) => {
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const lessonDispatch = useDesignerDataContext(c => c.dispatch);
  const presentationId = useDesignerDataContext(c => c.state?.localTemplate?.id);
  const selectedSlideId = useDesignerDataContext(selectedSlideIdSelector);
  const onAddElements = useDesignerDataContext(context => context.handleElementsAdd);
  const slides = useDesignerDataContext(c => c.state.localTemplate?.content.slides || []);
  const promptOutputs = useDesignerDataContext(allPromptOutputsSelector);
  const promptDependentInputs = useDesignerUIContext(
    context => context.state.promptDependentInputs,
  );
  const {
    generate,
    isLoading,
    reset: resetSingleTestCase,
    isSuccess: isSinglePromptTestCaseSuccess,
  } = useSinglePromptTestCase(prompt.id, presentationId);

  const updatePromptDependentInputs = useCallback(
    (payload: { id: string; value: string; type: PromptSingleTestCaseResultType }) => {
      dispatch({
        type: DesignerUIStateActions.updatePromptDependentInputs,
        payload,
      });
    },
    [dispatch],
  );

  const handleResourceChanged = (
    resource: string,
    model: string,
    imageOrientation: string,
  ) => {
    localTemplateDispatch({
      type: TemplateStateActions.editPrompt,
      prompt: {
        ...prompt,
        promptOutput: {
          ...prompt.promptOutput,
          resource: {
            type: resource as ResourceType,
            model: model as PromptModels,
            imageOrientation: imageOrientation as 'landscape' | 'portrait' | 'square',
          },
        },
      },
    });
  };

  const handleQueryChanged = (val: string) => {
    const queryMessage = prompt.messages.find(message => message.role === 'user');
    if (queryMessage) {
      localTemplateDispatch({
        type: TemplateStateActions.editPrompt,
        prompt: {
          ...prompt,
          messages: prompt.messages.map(message =>
            message.role === 'user'
              ? {
                  ...queryMessage,
                  content: val,
                }
              : message,
          ),
        },
      });
      resetSingleTestCase();
    }
  };
  const onAddAiImage = () => {
    if (!promptDependentInputs) return;
    if (
      [AIResourceTypes.openaiImage, AIResourceTypes.stableDiffusionImage].some(
        t => t === prompt.promptOutput.resource.type,
      )
    ) {
      const imageWidth =
        prompt.promptOutput.resource.imageOrientation === 'landscape'
          ? 1920
          : prompt.promptOutput.resource.imageOrientation === 'portrait'
          ? 650
          : 600;
      const imageHeight =
        prompt.promptOutput.resource.imageOrientation === 'landscape'
          ? 1080
          : prompt.promptOutput.resource.imageOrientation === 'portrait'
          ? 1080
          : 600;

      const newImageElement: SlideElement = createImageElement({
        mediaAltText: '',
        mediaDownloadLocation: undefined,
        mediaThumbnailUrl: undefined,
        mediaMimeType: 'image/png',
        mediaFileId: promptDependentInputs[prompt.id].value,
        width: imageWidth,
        height: imageHeight,
        x:
          prompt.promptOutput.resource.imageOrientation === 'landscape' ||
          prompt.promptOutput.resource.imageOrientation === 'portrait'
            ? 0
            : undefined,
        y:
          prompt.promptOutput.resource.imageOrientation === 'landscape' ||
          prompt.promptOutput.resource.imageOrientation === 'portrait'
            ? 0
            : undefined,
        preventRandomPlacement:
          prompt.promptOutput.resource.imageOrientation === 'landscape' ||
          prompt.promptOutput.resource.imageOrientation === 'portrait'
            ? true
            : false,
      });
      newImageElement.promptSourceId = prompt.id;
      newImageElement.imageObjectFit = 'cover';
      newImageElement.type = 'generatorImage';
      onAddElements([newImageElement], true);
    }
  };

  const { availablePromptDependencies } = useAvailablePromptDependencies({
    promptId: prompt.id,
    promptInputs,
    promptOutputs,
  });

  const dependencies = useMemo(() => findDependencies(prompt), [prompt]);

  const { visibleInputs } = useVisiblePromptTestCaseInputs({
    availablePromptDependencies,
    promptDependencies: dependencies,
    promptInputs,
  });

  const { visiblePromptDependentInputValues } = useVisiblePromptDependentInputValues({
    visibleInputs,
    promptDependentInputs,
  });

  const handleGenerateSinglePromptTestCase = useCallback(() => {
    if (!presentationId) {
      return;
    }

    let testCaseInputs: ById<string> = {};

    // add default values form the dependencies themselves
    dependencies.forEach(dependency => {
      const foundInput = promptInputs?.find(
        input => input.id === dependency.dependencyId,
      );

      if (foundInput && foundInput.defaultValue) {
        testCaseInputs = {
          ...testCaseInputs,
          [dependency.dependencyId]: foundInput.defaultValue as string,
        };
      }
    });

    // Overwrite the default values with the prompt dependent inputs
    Object.values(visiblePromptDependentInputValues).forEach(dependency => {
      testCaseInputs = {
        ...testCaseInputs,
        [dependency.id]: dependency.value,
      };
    });
    const inputData = Object.keys(testCaseInputs).filter(key => key.startsWith('input'));
    const outputData = Object.keys(testCaseInputs).filter(key =>
      key.startsWith('prompt'),
    );

    generate({
      presentationId,
      promptId: prompt.id,
      userInputFormData: inputData.map(key => ({
        id: key,
        type: 'string',
        value: testCaseInputs[key],
      })),
      resourceMap: outputData.reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            id: key,
            type: 'string',
            value: testCaseInputs[key],
          },
        }),
        {},
      ),
    });
  }, [
    dependencies,
    generate,
    presentationId,
    prompt.id,
    promptInputs,
    visiblePromptDependentInputValues,
  ]);

  useEffect(() => {
    if (!isSinglePromptTestCaseSuccess) {
      return; // Don't run
    }

    // Whenever a successfull test case is generated, we update all the slides with the latest output
    if (selectedSlideId) {
      const { slides: newSlides } = reconnectPromptDependantElements({
        promptResultData: promptDependentInputs || {},
        slides,
      });

      lessonDispatch({
        type: TemplateStateActions.allSlidesChanged,
        slides: newSlides,
        selectedSlideId: selectedSlideId,
      });
      resetSingleTestCase(); // make sure we reset the state to this doesn't trigger multiple times
    }
  }, [
    isSinglePromptTestCaseSuccess,
    slides,
    promptDependentInputs,
    lessonDispatch,
    selectedSlideId,
    resetSingleTestCase,
  ]);

  const queryMessage = useMemo(() => {
    const message = prompt.messages.find(message => message.role === 'user');
    return message?.content;
  }, [prompt]);

  const runTestcaseDisabled = useMemo(() => {
    //check if there is any input that has not been filled
    return visibleInputs.some(dependency => {
      return !visiblePromptDependentInputValues?.[dependency.id];
    });
  }, [visiblePromptDependentInputValues, visibleInputs]);

  return (
    <div
      style={{
        padding: '0px 20px 20px 20px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',

        gap: '10px',
      }}
    >
      <div>
        <AIImageConnectorConfirmModal promptImageResult={promptDependentInputs} />
        <Select
          label="Image source"
          value={prompt.promptOutput.resource.model}
          onSelect={val => {
            if (val === promptModels.dalle3) {
              handleResourceChanged(
                AIResourceTypes.openaiImage,
                val,
                prompt.promptOutput.resource.imageOrientation || 'square',
              );
            }
            if (val === promptModels.sd3Medium) {
              handleResourceChanged(
                AIResourceTypes.stableDiffusionImage,
                val,
                prompt.promptOutput.resource.imageOrientation || 'square',
              );
            }
            if (val === promptModels.sd35Medium) {
              handleResourceChanged(
                AIResourceTypes.stableDiffusionImage,
                val,
                prompt.promptOutput.resource.imageOrientation || 'square',
              );
            }
            if (val === promptModels.sd35LargeTurbo) {
              handleResourceChanged(
                AIResourceTypes.stableDiffusionImage,
                val,
                prompt.promptOutput.resource.imageOrientation || 'square',
              );
            }
            if (val === promptModels.sd35Large) {
              handleResourceChanged(
                AIResourceTypes.stableDiffusionImage,
                val,
                prompt.promptOutput.resource.imageOrientation || 'square',
              );
            }
          }}
          options={[
            {
              displayName: 'DALL·E 3 (4 🪙)',
              value: promptModels.dalle3,
            },
            {
              displayName: 'Stable Diffusion 3 (Medium) (3.5 🪙)',
              value: promptModels.sd3Medium,
            },
            {
              displayName: 'Stable Diffusion 3.5 (Medium) (3.5 🪙)',
              value: promptModels.sd35Medium,
            },
            {
              displayName: 'Stable Diffusion 3.5 (Large Turbo) (4 🪙)',
              value: promptModels.sd35LargeTurbo,
            },
            {
              displayName: 'Stable Diffusion 3.5 (Large) (6.5 🪙)',
              value: promptModels.sd35Large,
            },
          ]}
        />
        <Select
          label="Image orientation"
          value={prompt.promptOutput.resource.imageOrientation || 'square'}
          onSelect={val => {
            handleResourceChanged(
              prompt.promptOutput.resource.type,
              prompt.promptOutput.resource.model,
              val as 'landscape' | 'portrait' | 'square',
            );
          }}
          options={[
            {
              displayName: 'Landscape',
              value: 'landscape',
            },
            {
              displayName: 'Square',
              value: 'square',
            },
            {
              displayName: 'Portrait',
              value: 'portrait',
            },
          ]}
        />
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 10,
              paddingRight: 30,
            }}
          >
            <p style={{ marginBottom: 5, fontSize: 16 }}>{}</p>
            <Button
              type="ghost"
              styles={{
                textDecoration: 'underline',
                border: 'none',
                padding: '2px 10px',
                marginBottom: 5,
              }}
              onClick={() => setShowEditInputs(true)}
            >
              Edit inputs
            </Button>
          </div>
          <PromptTextEditor
            style={{ minHeight: '100px' }}
            availablePromptDependencies={availablePromptDependencies}
            placeholder="Enter query"
            value={queryMessage}
            onChange={val => {
              handleQueryChanged(val);
            }}
          />
        </div>
        <TestcaseContainer>
          <PromptTestcaseContainer>
            {visibleInputs.map(dependency => {
              const dependencyId = dependency?.id;
              if (!dependencyId) return null;
              if (dependency.formType === 'dropdown') {
                return (
                  <Select
                    label={dependency?.label}
                    placeholder={dependency?.defaultValue || 'Select value'}
                    onSelect={val => {
                      updatePromptDependentInputs({
                        id: dependencyId,
                        value: val as string,
                        type:
                          promptDependentInputs?.[dependencyId]?.type ||
                          dependency.formType,
                      });
                    }}
                    value={promptDependentInputs?.[dependencyId]?.value}
                    options={dependency?.constraints?.options || []}
                  ></Select>
                );
              }

              if (dependency.formType === 'longText') {
                return (
                  <div key={dependencyId}>
                    <TextArea
                      label={dependency?.label}
                      value={promptDependentInputs?.[dependencyId]?.value}
                      placeholder={
                        dependency?.defaultValue ||
                        promptDependentInputs?.[dependencyId]?.value ||
                        'Add a value'
                      }
                      style={{ width: '100%' }}
                      maxLength={dependency.constraints?.maxLength}
                      symbolCount={true}
                      onChange={e =>
                        updatePromptDependentInputs({
                          id: dependencyId,
                          value: e.target.value as string,
                          type:
                            promptDependentInputs?.[dependencyId]?.type ||
                            dependency.formType,
                        })
                      }
                    />
                  </div>
                );
              }
              return (
                <div key={dependencyId}>
                  <Input
                    label={dependency?.label}
                    value={promptDependentInputs?.[dependencyId]?.value}
                    placeholder={
                      dependency?.defaultValue ||
                      promptDependentInputs?.[dependencyId]?.value ||
                      'Add a value'
                    }
                    onChange={e => {
                      updatePromptDependentInputs({
                        id: dependencyId,
                        value: e.target.value as string,
                        type:
                          promptDependentInputs?.[dependencyId]?.type ||
                          dependency.formType,
                      });
                    }}
                  />
                </div>
              );
            })}
          </PromptTestcaseContainer>
        </TestcaseContainer>
        <Button
          loading={isLoading}
          onClick={() => handleGenerateSinglePromptTestCase()}
          disabled={runTestcaseDisabled}
        >
          Run test case
        </Button>
      </div>
      <Divider />
      <div
        style={{
          width: '100%',
          height: '300px',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            justifyContent: 'center',
            borderRadius: '10px',
          }}
        >
          {!promptDependentInputs && isLoading && (
            <LoadingOutlined style={{ fontSize: 40, color: 'black' }} spin />
          )}

          {promptDependentInputs && promptDependentInputs[prompt.id] && (
            <GameImageRenderer
              key={prompt.id}
              fileId={promptDependentInputs[prompt.id].value}
              fileMimetype={'image/png'}
              fileAltText={''}
              cropConfig={undefined}
              isDiscoverTemplate={false}
              isAuthenticated={true}
              isOwner={true}
              objectFit={'contain'}
              style={{ height: '100%', width: '100%' }}
            />
          )}
        </div>
      </div>

      {promptDependentInputs && promptDependentInputs[prompt.id] && (
        <PromptImageResults>
          <span style={{ width: '100%', textAlign: 'left', flex: 1 }}>Connect?</span>
          <Tooltip title="Connect with image on slide">
            <div>
              <PromptAIImageConnector promptImageSourceId={prompt.id} />
            </div>
          </Tooltip>
          <Tooltip title="Add as new image element">
            <AddTextButton onClick={() => onAddAiImage()} type="ghost">
              <IconWrapper height="16px" width="16px">
                <PlusSvg />
              </IconWrapper>
            </AddTextButton>
          </Tooltip>
        </PromptImageResults>
      )}
    </div>
  );
};

const AddTextButton = styled(Button)`
  border-radius: 50%;
  padding: 2px;
  height: 20px;
  width: 20px;
  margin-left: 5px;
  font-size: 14px;
  svg {
    margin: 0;
  }
`;

const PromptImageResults = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;
const MessageContainer = styled.div`
  & > *:not(:first-child) {
    margin-top: 5px;
  }
  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
`;
const TestcaseContainer = styled.div`
  margin: 10px 0;
`;

const PromptTestcaseContainer = styled(MessageContainer)``;

export default PromptImageBuilder;
